'use client';

import { NextIntlClientProvider, useLocale, useMessages } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';
import { useToasts } from '~/components/Toast';
import { SaveTripToastContent } from './SaveTripToastContent';
const DEFAULT_REVEAL_DEPTH_PERCENT = 10;
interface UseSaveTripToastArgs {
  tripId?: string;
  tripTitle: string;
  startDate?: string;
  endDate?: string;
  revealDepthPercent?: number;
  revealScrollUpPercent?: number;
}
export function useSaveTripToast({
  tripId,
  tripTitle,
  startDate,
  endDate,
  revealDepthPercent = DEFAULT_REVEAL_DEPTH_PERCENT,
  revealScrollUpPercent = 0
}: UseSaveTripToastArgs) {
  const toasts = useToasts();
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  // ToastProvider is mounted above the locale-aware [lang] layout, so toast content needs its
  // own NextIntlClientProvider (captured here, where the real locale is in scope) or it renders
  // in the root's default-locale context regardless of the page's actual locale.
  const locale = useLocale();
  const messages = useMessages();
  const enabled = process.env.NEXT_PUBLIC_FF_TRIP_SAVE_TOAST === 'true';

  // Latest trip data, read at add-time so the prompt always reflects (and saves) the current trip,
  // even after the traveller amends it while the prompt is open.
  const tripTitleRef = useRef(tripTitle);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  tripTitleRef.current = tripTitle;
  startDateRef.current = startDate;
  endDateRef.current = endDate;
  const firedRef = useRef(false);
  const dismissedRef = useRef(false);
  const reachedDepthRef = useRef(false);
  const lastYRef = useRef(0);
  const tickingRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const keyRef = useRef<string | null>(null);
  const thresholdRef = useRef(0);
  const scrollUpThresholdRef = useRef(0);
  const peakYRef = useRef(0);
  const showPrompt = useCallback(() => {
    if (!tripId) {
      return;
    }
    firedRef.current = true;
    keyRef.current = toastsRef.current.add({
      position: 'middle',
      width: 760,
      shadow: true,
      render: close => <NextIntlClientProvider locale={locale} messages={messages}>
          <SaveTripToastContent tripId={tripId} tripTitle={tripTitleRef.current} startDate={startDateRef.current} endDate={endDateRef.current} onDismiss={() => {
          dismissedRef.current = true;
          close();
        }} />
        </NextIntlClientProvider>
    });
  }, [tripId, locale, messages]);

  // Reveal-on-scroll-up. Keyed only on stable values so amending the trip never tears the prompt down.
  useEffect(() => {
    if (!enabled || !tripId) {
      return;
    }
    const measure = () => {
      const scrollable = document.documentElement.scrollHeight - globalThis.innerHeight;
      thresholdRef.current = scrollable > 0 ? scrollable * (revealDepthPercent / 100) : 0;
      scrollUpThresholdRef.current = Math.max(0, scrollable) * (revealScrollUpPercent / 100);
    };
    measure();
    const update = () => {
      tickingRef.current = false;
      rafIdRef.current = null;
      const y = globalThis.scrollY;
      const goingUp = y < lastYRef.current;
      lastYRef.current = Math.max(0, y);
      peakYRef.current = Math.max(peakYRef.current, y);
      if (thresholdRef.current > 0 && y >= thresholdRef.current) {
        reachedDepthRef.current = true;
      }
      if (firedRef.current || dismissedRef.current || !reachedDepthRef.current || !goingUp) {
        return;
      }
      if (peakYRef.current - y < scrollUpThresholdRef.current) {
        return;
      }
      showPrompt();
    };
    const onScroll = () => {
      if (!tickingRef.current) {
        rafIdRef.current = requestAnimationFrame(update);
        tickingRef.current = true;
      }
    };
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(document.body);
    globalThis.addEventListener('scroll', onScroll, {
      passive: true
    });
    globalThis.addEventListener('resize', measure, {
      passive: true
    });
    return () => {
      globalThis.removeEventListener('scroll', onScroll);
      globalThis.removeEventListener('resize', measure);
      resizeObserver.disconnect();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (keyRef.current) {
        toastsRef.current.close(keyRef.current);
      }
    };
  }, [enabled, tripId, showPrompt, revealDepthPercent, revealScrollUpPercent]);

  // Keep the prompt in sync when the traveller amends the trip (e.g. adds nights, changing the dates).
  // If it's on screen, re-add it so Save uses the current data; if it had gone, let scroll-up bring it back.
  useEffect(() => {
    if (!enabled || !tripId || dismissedRef.current) {
      return;
    }
    if (keyRef.current !== null) {
      toastsRef.current.close(keyRef.current);
      keyRef.current = null;
      showPrompt();
    } else if (firedRef.current) {
      firedRef.current = false;
    }
  }, [enabled, tripId, tripTitle, startDate, endDate, showPrompt]);
}